import React from "react";
import PropTypes from "prop-types";
import { Segment, Label, Icon, Loader, Message, Header, List } from "semantic-ui-react";
import moment from "moment";
import {
  RUN_STATUS_DISPLAY,
  TIMESTAMP_FORMAT,
  isTerminalStatus,
} from "./constants";
import ProgressBar from "../../../../../../../../../components/ProgressBar";

function ExtractedData({ data }) {
  if (data === null || data === undefined) {
    return null;
  }
  if (typeof data !== "object") {
    return <p>{String(data)}</p>;
  }
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <p>No account details returned.</p>;
  }
  return (
    <List>
      {entries.map(([key, value]) => (
        <List.Item key={key}>
          <strong>{key}:</strong>{" "}
          {typeof value === "object" ? JSON.stringify(value) : String(value)}
        </List.Item>
      ))}
    </List>
  );
}

ExtractedData.propTypes = {
  // Free-form vendor payload — shape is not known ahead of time.
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.any,
};

function statusDisplay(status) {
  return (
    RUN_STATUS_DISPLAY[status] || { color: "grey", label: status || "Unknown" }
  );
}

function RunCard({ run, expanded, live, onToggle, polling }) {
  const liveData = live && live.data;
  const effectiveStatus = (liveData && liveData.status) || run.status;

  // Status badge: show a loading spinner while the polled run awaits its first
  // backend response, an error state if that request failed (and we have no
  // data yet), and otherwise the confirmed live status (falling back to the
  // run's array status). Once live data exists it always wins, so re-polls of a
  // known status never flicker back to the spinner.
  function renderStatus() {
    if (!liveData && live && live.error) {
      return (
        <Label color="red" title={live.error.message}>
          <Icon name="exclamation triangle" /> Error
        </Label>
      );
    }
    if (!liveData && (polling || (live && live.loading))) {
      return (
        <Label>
          <Icon name="spinner" loading /> Loading
        </Label>
      );
    }
    const { color, label } = statusDisplay(effectiveStatus);
    return <Label color={color}>{label}</Label>;
  }

  function renderExpandedBody() {
    if (live && live.loading && !liveData) {
      return <Loader active inline="centered" />;
    }
    if (live && live.error && !liveData) {
      return (
        <Message negative size="small">
          <p>Could not load live status: {live.error.message}</p>
        </Message>
      );
    }
    if (!liveData) {
      return (
        <Message size="small">
          <p>Expand again to load this run&apos;s live status.</p>
        </Message>
      );
    }

    const hasProgress =
      typeof liveData.currentStep === "number" &&
      typeof liveData.totalSteps === "number";
    const warnings = Array.isArray(liveData.warnings) ? liveData.warnings : [];

    function renderScreenshot() {
      if (liveData.finalScreenshot) {
        return (
          <img
            src={`data:image/png;base64,${liveData.finalScreenshot}`}
            alt={`Final screenshot for run ${run.runId}`}
            style={{
              maxWidth: "100%",
              border: "1px solid #ddd",
              marginTop: "0.5rem",
            }}
          />
        );
      }
      if (isTerminalStatus(effectiveStatus)) {
        return (
          <Message size="small">
            <p>No screenshot available for this run.</p>
          </Message>
        );
      }
      return null;
    }

    return (
      <div>
        {hasProgress && (
          <ProgressBar
            label="Progress"
            value={liveData.currentStep}
            total={liveData.totalSteps}
            indicating={!isTerminalStatus(effectiveStatus)}
          />
        )}
        {liveData.errorMessage && (
          <Message negative size="small">
            <p>{liveData.errorMessage}</p>
          </Message>
        )}
        {warnings.length > 0 && (
          <Message warning size="small">
            <Message.Header>Warnings</Message.Header>
            <List bulleted>
              {warnings.map((warning, index) => (
                <List.Item key={`${index}-${warning}`}>{warning}</List.Item>
              ))}
            </List>
          </Message>
        )}
        {liveData.extractedData && (
          <Segment secondary>
            <Header as="h5">Created account details</Header>
            <ExtractedData data={liveData.extractedData} />
          </Segment>
        )}
        {renderScreenshot()}
      </div>
    );
  }

  return (
    <Segment>
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyPress={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          cursor: "pointer",
        }}
      >
        <Icon name={expanded ? "chevron down" : "chevron right"} />
        {renderStatus()}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            <strong>Run:</strong> {run.runId}
          </span>
          <span style={{ fontSize: "0.85em", color: "#888" }}>
            <strong>Workflow:</strong> {run.workflowId}
          </span>
        </div>
        <span style={{ marginLeft: "auto", color: "#888" }}>
          {run.createdAt && moment(run.createdAt).format(TIMESTAMP_FORMAT)}
        </span>
      </div>
      {expanded && (
        <div style={{ marginTop: "1rem" }}>{renderExpandedBody()}</div>
      )}
    </Segment>
  );
}

RunCard.propTypes = {
  run: PropTypes.shape({
    runId: PropTypes.string.isRequired,
    workflowId: PropTypes.string,
    status: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
  expanded: PropTypes.bool,
  live: PropTypes.shape({
    loading: PropTypes.bool,
    data: PropTypes.object,
    error: PropTypes.object,
  }),
  onToggle: PropTypes.func.isRequired,
  polling: PropTypes.bool,
};

export default RunCard;
